/**
 * Projects list page component.
 * Supports listing, creating, and deleting projects.
 * Each project is linked to a GitHub repository and can launch dev environments.
 *
 * @module pages/ProjectsPage
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  FolderGit2,
  Trash2,
  Plus,
  Search,
  ExternalLink,
  Box,
  ChevronRight,
  Copy,
  Check,
  Monitor,
  Terminal,
  Loader2,
  Play,
} from 'lucide-react';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import { useProjects, useDeleteProject, useSandboxes, useCreateSandbox } from '@/hooks';
import type { ProjectResponse, SandboxResponse, PresetSandboxType } from '@/types';

type Tab = 'repos' | 'workspaces';

const ENV_PRESETS: { key: PresetSandboxType; label: string; description: string }[] = [
  { key: 'GitHubCopilot', label: 'Copilot', description: 'GitHub Copilot agent' },
  { key: 'Claude', label: 'Claude', description: 'Anthropic Claude agent' },
  { key: 'UbuntuSystemd', label: 'Ubuntu', description: 'Ubuntu with systemd' },
];

const STATUS_STYLES: Record<string, { dot: string; label: string }> = {
  Running: { dot: 'bg-green-500', label: 'Running' },
  Starting: { dot: 'bg-yellow-400', label: 'Starting' },
  Stopped: { dot: 'bg-gray-400', label: 'Stopped' },
  Suspended: { dot: 'bg-blue-400', label: 'Suspended' },
  Error: { dot: 'bg-red-500', label: 'Error' },
  Failed: { dot: 'bg-red-500', label: 'Failed' },
};

function statusStyle(state?: string) {
  return STATUS_STYLES[state ?? ''] ?? { dot: 'bg-gray-400', label: state ?? 'Unknown' };
}

const TOKEN_BADGE: Record<string, { cls: string; label: string }> = {
  valid: {
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    label: 'Token valid',
  },
  'expiring-soon': {
    cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    label: 'Token expiring',
  },
  expired: {
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    label: 'Token expired',
  },
};

function tokenBadge(status?: string) {
  return (
    TOKEN_BADGE[status ?? ''] ?? {
      cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
      label: 'Token unknown',
    }
  );
}

function CopyLine({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center gap-2 group/line rounded px-2 py-1 bg-white dark:bg-[#0a0a0a] border border-gray-100 dark:border-[#222]">
      <span className="text-gray-400 dark:text-gray-500 select-none">$</span>
      <span className="flex-1 font-mono text-xs text-gray-700 dark:text-gray-300 truncate">
        {text}
      </span>
      <button
        onClick={copy}
        className="opacity-0 group-hover/line:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity flex-shrink-0"
        title="Copy"
      >
        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
}

/**
 * Projects list page
 */
export function ProjectsPage() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = useProjects();
  const { data: sandboxesData } = useSandboxes(1, 100);
  const deleteMutation = useDeleteProject();
  const createSandboxMutation = useCreateSandbox();

  const [tab, setTab] = useState<Tab>('repos');
  const [deleteTarget, setDeleteTarget] = useState<ProjectResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Tracks which project is currently launching an environment
  const [launchingProject, setLaunchingProject] = useState<string | null>(null);

  const sandboxes = sandboxesData ?? [];

  const filteredProjects = projects?.filter((project) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name.toLowerCase().includes(query) || project.repoName.toLowerCase().includes(query)
    );
  });

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create a project backed by a GitHub repo, then launch a dev environment to start coding.
          </p>
        </div>
        <Link
          to="/projects/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Project
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-[#2a2a2a]">
        <button
          onClick={() => setTab('repos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'repos'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Repos
        </button>
        <button
          onClick={() => setTab('workspaces')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'workspaces'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Environments
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-[#2a2a2a] bg-white dark:bg-[#161616] py-2 pl-10 pr-4 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Repos tab */}
      {tab === 'repos' && (
        <>
          {filteredProjects && filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
              {filteredProjects.map((project) => {
                const projectSandboxCount = sandboxes.filter(
                  (s: SandboxResponse) => s.labels?.projectId === project.id
                ).length;
                return (
                  <div
                    key={project.id}
                    className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-4 bg-white dark:bg-[#161616] hover:shadow-md dark:hover:border-[#3a3a3a] transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderGit2 className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {project.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {project.repoFullName}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <a
                          href={project.repoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                          title="Open repo on GitHub"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <button
                          onClick={() => setDeleteTarget(project)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                          title="Delete project"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Created {new Date(project.createdAt).toLocaleDateString()}
                      </p>
                      {(() => {
                        const { cls, label } = tokenBadge(project.tokenStatus);
                        return (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}
                            title={
                              project.tokenExpiresAt
                                ? `Expires ${new Date(project.tokenExpiresAt).toLocaleDateString()}`
                                : undefined
                            }
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </div>
                    {/* Running environments count */}
                    {projectSandboxCount > 0 ? (
                      <button
                        onClick={() => setTab('workspaces')}
                        className="mt-3 flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        <Box className="h-3 w-3 flex-shrink-0" />
                        <span className="text-xs">
                          {projectSandboxCount} environment{projectSandboxCount !== 1 ? 's' : ''}
                        </span>
                      </button>
                    ) : (
                      <div className="mt-3 flex items-center gap-1.5">
                        <Box className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          No environments yet
                        </span>
                      </div>
                    )}

                    {/* Environment preset picker */}
                    <div className="mt-3 space-y-1.5">
                      <p className="text-[11px] text-gray-500 dark:text-gray-500 uppercase tracking-wide font-medium">
                        Launch environment
                      </p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {ENV_PRESETS.map((preset) => {
                          const isLaunching = launchingProject === `${project.id}:${preset.key}`;
                          return (
                            <button
                              key={preset.key}
                              disabled={!!launchingProject || createSandboxMutation.isPending}
                              onClick={async () => {
                                setLaunchingProject(`${project.id}:${preset.key}`);
                                try {
                                  const sandbox = await createSandboxMutation.mutateAsync({
                                    presetSandboxType: preset.key,
                                    projectId: project.id,
                                  });
                                  navigate(`/sandboxes/${encodeURIComponent(sandbox.id)}`);
                                } catch {
                                  // Error toast is handled by useCreateSandbox hook
                                } finally {
                                  setLaunchingProject(null);
                                }
                              }}
                              className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-gray-200 dark:border-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#3a3a3a] disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium rounded-md transition-colors"
                              title={preset.description}
                            >
                              {isLaunching ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                              {preset.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Dev locally quick setup */}
                    <details className="mt-3 group/dev">
                      <summary className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                        <ChevronRight className="h-3 w-3 transition-transform group-open/dev:rotate-90" />
                        Develop locally
                      </summary>
                      <div className="mt-2 rounded-md bg-gray-50 dark:bg-[#111] border border-gray-200 dark:border-[#2a2a2a] p-3 space-y-3">
                        {/* Clone + VS Code */}
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Monitor className="h-3 w-3 text-blue-400" />
                            <p className="text-xs font-medium text-gray-300">Open in VS Code</p>
                          </div>
                          <div className="space-y-1.5">
                            <CopyLine text={`git clone ${project.repoUrl}.git`} />
                            <CopyLine text={`cd ${project.repoName} && code .`} />
                          </div>
                        </div>

                        {/* Clone + terminal */}
                        <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-3">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Terminal className="h-3 w-3 text-green-400" />
                            <p className="text-xs font-medium text-gray-300">
                              Or just use the terminal
                            </p>
                          </div>
                          <div className="space-y-1.5">
                            <CopyLine
                              text={`git clone ${project.repoUrl}.git && cd ${project.repoName}`}
                            />
                          </div>
                        </div>

                        {/* Push changes */}
                        <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-3">
                          <p className="text-xs font-medium text-gray-300 mb-1.5">
                            Push your changes
                          </p>
                          <div className="space-y-1.5">
                            <CopyLine text='git add -A && git commit -m "my changes" && git push' />
                          </div>
                        </div>

                        {/* Sync note */}
                        <p className="text-[11px] text-gray-500 dark:text-gray-600 leading-relaxed border-t border-gray-200 dark:border-[#2a2a2a] pt-2">
                          Changes you push will be available the next time you create a sandbox from
                          this project. Already-running sandboxes can pull with{' '}
                          <code className="font-mono text-gray-400">git pull</code>.
                        </p>
                      </div>
                    </details>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 dark:border-[#2a2a2a] rounded-lg p-12 text-center">
              <FolderGit2 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">No projects yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Create a project to get a GitHub repo and launch dev environments from it.
              </p>
              <Link
                to="/projects/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Project
              </Link>
            </div>
          )}
        </>
      )}

      {/* Workspaces tab */}
      {tab === 'workspaces' && (
        <div className="space-y-4">
          {filteredProjects && filteredProjects.length > 0 ? (
            filteredProjects.map((project) => {
              const projectSandboxes = sandboxes.filter(
                (s: SandboxResponse) => s.labels?.projectId === project.id
              );
              return (
                <div
                  key={project.id}
                  className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg bg-white dark:bg-[#161616]"
                >
                  {/* Project header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-[#2a2a2a]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderGit2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {project.name}
                      </span>
                      <a
                        href={project.repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                        title={project.repoFullName}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {ENV_PRESETS.map((preset) => {
                        const isLaunching = launchingProject === `${project.id}:${preset.key}`;
                        return (
                          <button
                            key={preset.key}
                            disabled={!!launchingProject || createSandboxMutation.isPending}
                            onClick={async () => {
                              setLaunchingProject(`${project.id}:${preset.key}`);
                              try {
                                const sandbox = await createSandboxMutation.mutateAsync({
                                  presetSandboxType: preset.key,
                                  projectId: project.id,
                                });
                                navigate(`/sandboxes/${encodeURIComponent(sandbox.id)}`);
                              } catch {
                                // Error toast handled by hook
                              } finally {
                                setLaunchingProject(null);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-medium rounded transition-colors"
                            title={preset.description}
                          >
                            {isLaunching ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sandboxes */}
                  {projectSandboxes.length > 0 ? (
                    <div className="divide-y divide-gray-100 dark:divide-[#2a2a2a]">
                      {projectSandboxes.map((sandbox: SandboxResponse) => {
                        const { dot, label } = statusStyle(sandbox.state);
                        return (
                          <Link
                            key={sandbox.id}
                            to={`/sandboxes/${sandbox.id}`}
                            className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#1e1e1e] transition-colors"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                              <span className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
                                {sandbox.id.slice(0, 8)}
                              </span>
                              <span className="text-xs text-gray-400">{label}</span>
                            </div>
                            <span className="text-xs text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2">
                              Open →
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-4 py-4 text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        No environments yet — launch one from the Repos tab
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="border border-dashed border-gray-300 dark:border-[#2a2a2a] rounded-lg p-12 text-center">
              <FolderGit2 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">No projects yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Create a project to get started with a dev environment.
              </p>
              <Link
                to="/projects/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Project
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Delete project "${deleteTarget?.name}"? This will not delete the GitHub repo.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
